import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import {
  LayoutDashboard, Users, TrendingUp, CreditCard, User, LogOut, Menu, X,
  Sun, Moon, Shield, Server, ArrowDownToLine, Settings, BarChart3, MessageSquare,Mail,
  Activity, Coins, CircleDollarSign, ShieldCheck
} from 'lucide-react';
import { MetaTradifyLogo } from '@/components/MetaTradifyLogo';

const navGroups = [
  {
    label: 'Operations',
    items: [
      { path: '/admin', label: 'Overview', icon: LayoutDashboard, exact: true },
      { path: '/admin/users', label: 'Users', icon: Users },
      // { path: '/admin/trades', label: 'Trades', icon: TrendingUp },
    ],
  },
  {
    label: 'Finance',
    items: [
      { path: '/admin/credit-report', label: 'Credit Usage', icon: Coins },
      { path: '/admin/spread', label: 'Spread Management', icon: CircleDollarSign },
      { path: '/admin/deposits-withdrawals', label: 'Deposits & Withdrawals', icon: ArrowDownToLine },
      { path: '/admin/leverage-funds', label: 'Leverage Funds', icon: BarChart3 },
      { path: '/admin/payment-settings', label: 'Payment Settings', icon: Settings },
      // { path: '/admin/payments', label: 'Payments (Old)', icon: CreditCard },
    ],
  },
  {
    label: 'Controls',
    items: [
      { path: '/admin/kyc', label: 'KYC Verification', icon: Shield },
      { path: '/admin/broker', label: 'Broker Controls', icon: Server },
      { path: '/admin/simulation', label: 'Trade Controls', icon: Activity },
      { path: '/admin/tickets', label: 'Support Tickets', icon: MessageSquare },
      { path: '/admin/send-emails', label: 'Send Mail', icon: Mail },
    ],
  },
  {
    label: 'Account',
    items: [
      { path: '/admin/profile', label: 'Profile', icon: User },
    ],
  },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isPathActive = (item) => item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
  const allItems = navGroups.flatMap(g => g.items);
  const currentItem = allItems.find(isPathActive);

  return (
    <div className="min-h-screen bg-[var(--bg-page)] text-[var(--text-primary)]" data-testid="admin-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Single Sidebar */}
      <aside
        className={`fixed z-50 flex flex-col transition-transform duration-300
          inset-y-0 left-0 w-[280px] bg-[var(--bg-sidebar)] border-r border-[var(--border-default)]
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-[110%] lg:translate-x-0'}`}
      >
        <div className="px-5 pt-5 pb-4 border-b border-[var(--border-subtle)] flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <MetaTradifyLogo size={22} />
            </div>
            <div>
              <span className="text-[17px] font-bold text-[var(--text-primary)] tracking-tight block leading-tight">metaTradify</span>
              <span className="text-[10px] font-semibold text-emerald-500 uppercase tracking-[0.16em]">Admin Panel</span>
            </div>
          </Link>
          <button className="lg:hidden text-[var(--text-secondary)]" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.label} className="space-y-0.5">
              <p className="px-3 pb-1.5 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-[0.12em]">{group.label}</p>
              {group.items.map((item) => {
                const active = isPathActive(item);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors ${
                      active
                        ? 'bg-[var(--accent-soft)] text-[var(--accent-text)] font-semibold'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover-bg)]'
                    }`}
                  >
                    <Icon className="w-[18px] h-[18px] shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="px-3 pb-4 pt-3 border-t border-[var(--border-subtle)] space-y-1">
          <button
            onClick={toggleTheme}
            data-testid="admin-theme-toggle"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover-bg)] w-full transition-colors"
          >
            {theme === 'dark' ? <Sun className="w-[18px] h-[18px] text-amber-400" /> : <Moon className="w-[18px] h-[18px]" />}
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] font-medium text-[var(--text-secondary)] hover:text-red-500 hover:bg-red-500/10 w-full transition-colors"
          >
            <LogOut className="w-[18px] h-[18px]" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-[280px] flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 glass border-b border-[var(--border-default)]">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2 rounded-lg hover:bg-[var(--hover-bg)] text-[var(--text-secondary)]" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-mobile-menu">
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <div className="hidden md:flex items-center gap-2 text-[15px]">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span className="text-[var(--text-tertiary)]">Admin</span>
                <span className="text-[var(--text-tertiary)]">/</span>
                <span className="text-[var(--text-primary)] font-semibold">{currentItem?.label || 'Dashboard'}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg bg-[var(--hover-bg)] border border-[var(--border-default)]">
                <div className="w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{user?.name?.[0]?.toUpperCase()}</span>
                </div>
                <div className="hidden sm:block">
                  <p className="text-xs font-medium text-[var(--text-primary)] leading-tight">{user?.name}</p>
                  <p className="text-[10px] text-emerald-500 leading-tight">Administrator</p>
                </div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
